import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { select, Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';
import { PreloaderColor } from '@enums/shared.enum';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UiKitModule } from '../../shared/ui-kit/ui-kit.module';
import { AppState } from '../../store/app.state';
import { AlarmsSelectors } from '@states/alarm-mangment/alarm-management.selectros';
import { AlarmManagementModel } from '@models/alarm-management.model';
import { AlarmManagementAction } from '@states/alarm-mangment/alarm-management.action-types';
import { LocationSelectors } from '@states/location/location.selector-types';

@Component({
  selector: 'app-alarms-management',
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    UiKitModule,
    MatIconModule,
    MatTooltipModule,
    ScrollingModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './alarms-management.component.html',
  styleUrl: './alarms-management.component.scss',
})
export class AlarmsManagementComponent implements OnInit {
  @ViewChild('noActiveAlarmsContent') noActiveAlarmsContent!: TemplateRef<any>;
  @ViewChild('noCanceledAlarmsContent') noCanceledAlarmsContent!: TemplateRef<any>;

  public activeAlarms$: Observable<AlarmManagementModel.AlarmData[]> =
    this.store$.pipe(
      select(AlarmsSelectors.selectActiveAlarms),
      tap(data => {}),
    );

  public canceledAlarms$: Observable<AlarmManagementModel.AlarmData[]> =
    this.store$.pipe(
      select(AlarmsSelectors.selectCanceledAlarms),
      tap(data => {}),
    );
  public locationName$(locationId: string) {
    return this.store$.select(LocationSelectors.selectLocationName(locationId));
  }
  public isLoading$: Observable<boolean> =
    this.store$.pipe(select(AlarmsSelectors.selectIsLoading));

  public activeAlarmsCount$: Observable<number> =
    this.store$.pipe(select(AlarmsSelectors.selectActiveAlarmsCount));

  public loaderColor = PreloaderColor;
  public AlarmStatus = AlarmManagementModel.AlarmStatus;

  public createAlarmForm: FormGroup;

  constructor(
    private store$: Store<AppState>,
    private dialog: MatDialog,
    private fb: FormBuilder,
  ) {
    this.createAlarmForm = this.fb.group({
      locationId: [''],
      uniformId: ['']
    });
  }

  ngOnInit() {
    this.loadAlarms();
  }

  loadAlarms() {
    this.store$.dispatch(AlarmManagementAction.startGetActiveAlarms());
    this.store$.dispatch(AlarmManagementAction.startGetCanceledAlarms());
  }

  onCreateAlarm() {
    if (this.createAlarmForm.valid) {
      const createAlarmData: AlarmManagementModel.CreateAlarm = {
        locationId: this.createAlarmForm.value.locationId,
        uniformId: this.createAlarmForm.value.uniformId || undefined
      };

      this.store$.dispatch(AlarmManagementAction.createAlarm({ createAlarm: createAlarmData }));
      this.createAlarmForm.reset();
    }
  }

  cancelAlarmById(alarmId: string) {
    this.store$.dispatch(AlarmManagementAction.cancelAlarm({ alarmId }));
  }

  refresh() {
    this.loadAlarms();
  }

  getStatusText(status: AlarmManagementModel.AlarmStatus): string {
    return status === AlarmManagementModel.AlarmStatus.ACTIVE ? 'Active' : 'Handled';
  }

  getStatusClass(status: AlarmManagementModel.AlarmStatus): string {
    return status === AlarmManagementModel.AlarmStatus.ACTIVE ? 'status-active' : 'status-handled';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  getTimeDuration(startTime: string, endTime?: string): string {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }
}
